<template>
  <div
    :class="['save-view-actions', { '-vertical': layout === 'vertical' }, { '-mobile': $store.state.breakpoint.smAndDown }]"
  >
    <m-btn
      small
      :hide-border="!$store.state.breakpoint.smAndDown"
      light
      class="_reset-btn"
      :align-left="layout === 'vertical'"
      data-cy="reset-btn"
      @click="resetView(currentView.uid)"
    >
      {{ resetBtnLabel }}
    </m-btn>
    <m-tooltip
      v-if="currentView.canEdit"
      :placement="layout === 'vertical' ? 'left' : 'top'"
      class="_save-btn"
    >
      <m-btn
        small
        :outlined="layout !== 'vertical'"
        color="warning"
        block
        :hide-border="layout === 'vertical'"
        :align-left="layout === 'vertical'"
        :loading="viewsService.loadingUpdateView.value"
        @click="updateView(currentView)"
      >
        {{ saveBtnLabel }}
      </m-btn>
      <template #title>
        {{ saveBtnTooltip }}
      </template>
    </m-tooltip>
    <template v-else>
      <m-tooltip :placement="layout === 'vertical' ? 'left' : 'top'">
        <m-btn
          icon="exclamation-circle-filled"
          small
          light
          hide-border
          disabled
        >
          {{ $t('savedViewSelector.canNotSave') }}
        </m-btn>
        <template #title>
          {{ $t('savedViewSelector.canNotSaveNote') }}
        </template>
      </m-tooltip>
    </template>
  </div>
</template>

<script setup>
import useSnackBar from '@/composables/snackbar';
import { computed } from 'vue';
import { logCatch } from '@/lib/logger/logger';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  viewsService: {
    type: Object,
    required: true,
  },
  layout: {
    type: String,
    default: 'horizontal',
  },
  onlyParams: {
    type: Array,
    default: undefined,
  },
});

const currentView = props.viewsService.currentView;

const { t } = useI18n();

const snackBar = useSnackBar();

const resetBtnLabel = computed(() => {
  if (props.onlyParams !== undefined) {
    if (props.onlyParams.includes('filter')) {
      return t('savedViewSelector.resetFilter');
    }
    if (props.onlyParams.includes('order')) {
      return t('savedViewSelector.resetSorter');
    }
  }
  return t('savedViewSelector.reset');
});

const saveBtnLabel = computed(() => {
  if (currentView.value.isPublic) {
    return t('savedViewSelector.saveForEveryone');
  }
  return t('general.save');
});

const saveBtnTooltip = computed(() => {
  if (currentView.value.isPublic) {
    return t('savedViewSelector.saveButtonTooltipPublic');
  }
  return t('savedViewSelector.saveButtonTooltipPrivate');
});

const resetView = (id) => props.viewsService.resetView(id, { onlyParams: props.onlyParams });
const updateView = (view) => {
  props.viewsService.updateView(view, { onlyParams: props.onlyParams }).then(() => {
    snackBar.success(t('success.saved'));
  }).catch(logCatch(() => {
    snackBar.error(t('error.default'));
  }));
};

</script>

<style scoped lang="scss">
.save-view-actions {
  display: flex;
  gap: 0.4rem;

  &.-vertical {
    flex-direction: column;
  }

  &.-mobile {
    flex-direction: row-reverse;

    ._save-btn {
      width: 100%;
    }
  }
}
</style>
